import { upperFirst } from 'es-toolkit';
import React, { useCallback } from 'react';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, Button, Checkbox, FormControlLabel, Grid, IconButton, Typography } from '@mui/material';

import {
  formatLengthToArchitecturalString,
  INDETERMINATE_TEXT,
  IndeterminateValue,
  isIndeterminate,
  TooltipIconButton,
} from '@paragon/client-tools';
import { Direction3D, OrderedCuts, Point3D, TopOrBottomCut } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  beginTrussEnvelopeCutPicking,
  deleteTrussEnvelopeCut,
  deleteTrussEnvelopeOrderedCuts,
  finishCuttingEnvelope,
  setTrussEnvelopeOrderedCutsReversal,
} from '../../../../../store/actions/layout/trussEnvelopeActions';
import { selectItemByGuid } from '../../../../../store/actions/selectActions';
import { getPickingObjectInfo } from '../../../../../store/selectors/pickingSelectors';
import { TrussEnvelopeTargetInfo } from '../../../../../store/slices/applicationSlice';
import { layoutSlice } from '../../../../../store/slices/layoutSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';

function formatPoint3D({ x, y, z }: Point3D) {
  return `(${formatLengthToArchitecturalString(x)}, ${formatLengthToArchitecturalString(
    y,
  )}, ${formatLengthToArchitecturalString(z)})`;
}

function formatDirection({ x, y, z }: Direction3D) {
  return `(${x.toFixed(2)}, ${y.toFixed(2)}, ${z.toFixed(2)})`;
}

interface Props {
  readonly topCuts: readonly TopOrBottomCut[];
  readonly hiddenTopCutCount?: number;

  readonly bottomCuts: readonly TopOrBottomCut[];
  readonly hiddenBottomCutCount?: number;

  readonly orderedCutProps?: {
    readonly orderedTopCuts: OrderedCuts | IndeterminateValue;
    readonly orderedBottomCuts: OrderedCuts | IndeterminateValue;
  };

  readonly targetInfo: TrussEnvelopeTargetInfo;

  readonly hideTitle?: boolean;

  readonly disabled?: boolean;
}

export function TrussEnvelopeAdditionalCutsProperties({
  topCuts,
  hiddenTopCutCount,
  bottomCuts,
  hiddenBottomCutCount,
  orderedCutProps,
  targetInfo,
  hideTitle,
  disabled,
}: Props) {
  return (
    <>
      {!hideTitle && <Typography variant="h6">Additional Cuts</Typography>}

      <Grid container spacing={1}>
        <PlaneCutsList
          cuts={topCuts}
          hiddenCutCount={hiddenTopCutCount}
          targetInfo={targetInfo}
          topOrBottom="top"
          disabled={disabled}
        />

        <PlaneCutsList
          cuts={bottomCuts}
          hiddenCutCount={hiddenBottomCutCount}
          targetInfo={targetInfo}
          topOrBottom="bottom"
          disabled={disabled}
        />

        {orderedCutProps && (
          <>
            <OrderedCutsList
              topOrBottom="top"
              cuts={orderedCutProps.orderedTopCuts}
              targetInfo={targetInfo}
              disabled={disabled}
            />

            <OrderedCutsList
              topOrBottom="bottom"
              cuts={orderedCutProps.orderedBottomCuts}
              targetInfo={targetInfo}
              disabled={disabled}
            />
          </>
        )}
      </Grid>
    </>
  );
}

interface OrderedCutsListProps {
  readonly cuts: OrderedCuts | IndeterminateValue;
  readonly targetInfo: TrussEnvelopeTargetInfo;
  readonly topOrBottom: 'top' | 'bottom';
  readonly disabled?: boolean;
}

function OrderedCutsList({ cuts, targetInfo, topOrBottom, disabled }: OrderedCutsListProps) {
  return (
    <PlaneCutsList
      orderedProps={isIndeterminate(cuts) ? cuts : { reversed: cuts.reversed }}
      cuts={isIndeterminate(cuts) ? [] : cuts.reversed ? cuts.cuts.toReversed() : cuts.cuts}
      targetInfo={targetInfo}
      topOrBottom={topOrBottom}
      disabled={disabled}
    />
  );
}

interface TopOrBottomCutsListProps {
  readonly cuts: readonly TopOrBottomCut[];
  readonly hiddenCutCount?: number;
  readonly targetInfo: TrussEnvelopeTargetInfo;
  readonly orderedProps?: { readonly reversed: boolean } | IndeterminateValue;
  readonly topOrBottom: 'top' | 'bottom';
  readonly disabled?: boolean;
}

function PlaneCutsList({
  cuts,
  hiddenCutCount,
  targetInfo,
  topOrBottom,
  orderedProps,
  disabled,
}: TopOrBottomCutsListProps) {
  const dispatch = useAppDispatch();

  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);
  const isPickingTheseOrderedCuts =
    pickingObjectInfo?.type === 'pickingPlane' &&
    ((pickingObjectInfo.targetInfo.type === 'truss-envelope-ordered-top-cut' && topOrBottom === 'top') ||
      (pickingObjectInfo.targetInfo.type === 'truss-envelope-ordered-bottom-cut' && topOrBottom === 'bottom'));

  const isPickingTheseCuts =
    pickingObjectInfo?.type === 'pickingPlane' &&
    ((pickingObjectInfo.targetInfo.type === 'truss-envelope-top-cut' && topOrBottom === 'top') ||
      (pickingObjectInfo.targetInfo.type === 'truss-envelope-bottom-cut' && topOrBottom === 'bottom'));

  const cutAction = orderedProps
    ? isPickingTheseOrderedCuts
      ? 'Finish Cutting'
      : cuts.length !== 0 || isIndeterminate(orderedProps)
        ? 'Re-Cut'
        : 'Add Cuts'
    : isPickingTheseCuts
      ? 'Finish Cutting'
      : 'Add Cut';

  return (
    <Grid size={6} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography gutterBottom variant="subtitle2">
          {orderedProps ? 'Ordered' : ''} {upperFirst(topOrBottom)} Cuts
          {(hiddenCutCount ?? 0) > 0 && ` (${hiddenCutCount} not shown)`}
          {isIndeterminate(orderedProps) && ` (${INDETERMINATE_TEXT}; not shown)`}
        </Typography>

        {orderedProps && (
          <TooltipIconButton title={getHelpText(topOrBottom)}>
            <HelpIcon fontSize="small" />
          </TooltipIconButton>
        )}
      </Box>

      {orderedProps && (
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={!isIndeterminate(orderedProps) && orderedProps.reversed}
                onChange={async (event) =>
                  await dispatch(setTrussEnvelopeOrderedCutsReversal(targetInfo, topOrBottom, event.target.checked))
                }
              />
            }
            label="Right-to-Left"
            disabled={disabled || isIndeterminate(orderedProps)}
          />
        </Box>
      )}

      {cuts.map((cut, index) => (
        <PlaneCutItem
          key={index}
          cut={cut}
          cutIndex={index}
          targetInfo={targetInfo}
          ordered={!!orderedProps}
          topOrBottom={topOrBottom}
          disabled={disabled}
        />
      ))}

      <Box sx={{ flexGrow: 1 }} />

      <Button
        fullWidth
        variant={cutAction === 'Finish Cutting' ? 'contained' : 'outlined'}
        className="clickable-during-picking"
        startIcon={cutAction === 'Finish Cutting' ? <DoneAllIcon /> : <ContentCutIcon />}
        onClick={async () => {
          if (cutAction === 'Finish Cutting') {
            dispatch(finishCuttingEnvelope());
            return;
          }

          if (cutAction === 'Re-Cut') {
            await dispatch(deleteTrussEnvelopeOrderedCuts(targetInfo, topOrBottom));
          }

          const ordered = orderedProps != null;

          dispatch(beginTrussEnvelopeCutPicking(targetInfo, topOrBottom, ordered));
        }}
        disabled={disabled}
      >
        {cutAction}
      </Button>
    </Grid>
  );
}

function getHelpText(topOrBottom: 'top' | 'bottom'): string {
  return `
  Cuts specified here are applied in order from left to right (by default) to form facets of the truss solid. Any cuts specified here
  override other items forming the ${topOrBottom} of the truss solid, including ${topOrBottom} cuts specified above and
  cap levels.
  `;
}

interface TopOrBottomCutItemProps {
  readonly cut: TopOrBottomCut;
  readonly cutIndex: number;
  readonly targetInfo: TrussEnvelopeTargetInfo;
  readonly ordered?: boolean;
  readonly topOrBottom: 'top' | 'bottom';
  readonly disabled?: boolean;
}

function PlaneCutItem({ cut, cutIndex, targetInfo, ordered, topOrBottom, disabled }: TopOrBottomCutItemProps) {
  const dispatch = useAppDispatch();

  const hoverCutFacet = useCallback(() => {
    if (targetInfo.target === 'insertionTrussEnvelope') return; // Cut facets on the insertion envelope are currently unsupported
    dispatch(layoutSlice.actions.cutFacetHovered({ targetInfo, topOrBottom, cutIndex }));
  }, [targetInfo, dispatch, topOrBottom, cutIndex]);

  const clearCutFacet = useCallback(() => dispatch(layoutSlice.actions.cutFacetHovered(null)), [dispatch]);

  return (
    <Box sx={{ display: 'flex' }}>
      {cut.type === 'Absolute' ? (
        ordered ? (
          <Button
            // No click functionality, just hover
            onPointerOver={hoverCutFacet}
            onPointerOut={clearCutFacet}
            disabled={disabled}
          >
            Cut #{cutIndex + 1}
          </Button>
        ) : (
          <>
            <Typography>Point: {formatPoint3D(cut.plane.basePoint)}</Typography>
            <Typography>Normal Direction: {formatDirection(cut.plane.normalDirection)}</Typography>
          </>
        )
      ) : cut.type === 'RoofPlane' ? (
        <Button
          onClick={() => {
            clearCutFacet();
            dispatch(selectItemByGuid(cut.roofPlaneGuid, 'roofPlane'));
          }}
          onPointerOver={() => {
            hoverCutFacet();
            dispatch(layoutSlice.actions.highlightedRoofPlaneSet(cut.roofPlaneGuid));
          }}
          onPointerOut={() => {
            clearCutFacet();
            dispatch(layoutSlice.actions.highlightedPlanesCleared());
          }}
          disabled={disabled}
        >
          Cut #{cutIndex + 1}
        </Button>
      ) : cut.type === 'CeilingPlane' ? (
        <Button
          onClick={() => {
            clearCutFacet();
            dispatch(selectItemByGuid(cut.ceilingPlaneGuid, 'ceilingPlane'));
          }}
          onPointerOver={() => {
            hoverCutFacet();
            dispatch(layoutSlice.actions.highlightedCeilingPlaneSet(cut.ceilingPlaneGuid));
          }}
          onPointerOut={() => {
            clearCutFacet();
            dispatch(layoutSlice.actions.highlightedPlanesCleared());
          }}
          disabled={disabled}
        >
          Cut #{cutIndex + 1}
        </Button>
      ) : (
        assertExhaustiveSwitch(cut)
      )}

      {!ordered && (
        <IconButton
          sx={{ marginLeft: 'auto' }}
          onClick={async () => await dispatch(deleteTrussEnvelopeCut(targetInfo, topOrBottom, cut))}
          disabled={disabled}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}
